import { Toaster } from 'sonner';
import { QueryClientProvider } from '@tanstack/react-query';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';
import { queryClient } from '@shared/lib/queryClient';
import { AuthProvider } from '@shared/contexts/AuthContext';
import { ThemeProvider } from '@shared/components/common/theme-provider';
import { PublicRoute } from '@shared/components/route/PublicRoute';
import { useAuth } from '@shared/contexts/AuthContext';
import { ReactQueryDevTools } from '@shared/components/common/ReactQueryDevTools';
import React, { Suspense } from 'react';
import LoginPage from './pages/LoginPage';

// Marketing page is only for logged-out visitors — keep it out of the app bundle.
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
import NotesWorkspace from './pages/NotesWorkspace';
import TasksPage from './pages/TasksPage';
import SyncHistoryPage from './pages/SyncHistoryPage';
import OrgPage from './pages/OrgPage';
import ProfilePage from './pages/ProfilePage';

// Extension-style layout: no sidebar — each page brings its own top nav.
function NotesLayout() {
  return (
    <Routes>
      <Route path="/" element={<NotesWorkspace />} />
      <Route path="/notes/:note_id" element={<NotesWorkspace />} />
      {/* Old folder-page IA — folders now live in the notes panel */}
      <Route path="/folder/:folder_slug" element={<Navigate to="/" replace />} />
      <Route path="/tasks" element={<TasksPage />} />
      <Route path="/sync" element={<SyncHistoryPage />} />
      <Route path="/org" element={<OrgPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// GA4 only counts the initial page load in an SPA — report route changes too.
// Skips the first render (the gtag config call already sent that page_view).
function AnalyticsTracker() {
  const location = useLocation();
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

// Public marketing landing for logged-out visitors; the app for logged-in users.
function Root() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <NotesLayout />;
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <LandingPage />
    </Suspense>
  );
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="hintword-theme">
        <AuthProvider>
          <div className="min-h-screen bg-background text-foreground">
            <Toaster />
            <AnalyticsTracker />
            <Routes>
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/*" element={<Root />} />
            </Routes>
          </div>
          <ReactQueryDevTools />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
